import React, { useState, useEffect, useCallback, useRef } from 'react';

import { Col, Radio, Select, Switch, Tooltip } from 'antd';

import CustomMaskedInput from 'components/MaskedInput';
import { constants } from 'helpers/constants';
import type IProduct from 'interfaces/IProduct';
import BlockedFieldTooltipProps from 'scenes/Producer/Product/EditProduct/components/General/blockedTooltipProps';
import { recurrencyFrequencyType } from 'scenes/Producer/Product/EditProduct/components/Sales/components/Subscription';
import { EChargeType, EProductType } from 'v2/interfaces/product';
import { run } from 'validations/ruleRunner';

import PermanentAccessAlertModal from './PermanentAccessAlertModal';
import Validations from './validations';

interface IProps {
  product: IProduct;
  enabledValidation: boolean;
  handleChange: any;
  setRefField: any;
  setRefError: any;
  isAlumy?: boolean;
}

const CUSTOM_RECURRENCY = 'P';
const OPEN_PRICE_BLOCK_MESSAGE = 'Indisponível quando Valor Aberto está ativo.';

function Subscription(props: IProps) {
  const { handleChange, product, setRefField, isAlumy, enabledValidation } = props;

  const [validationErrors, setValidationErrors] = useState<any>([]);
  const [showPermanentAccessAlertModal, setShowPermanentAccessAlertModal] = useState(false);

  const isFirstRenderRef = useRef(true);
  const prevPropsRef = useRef({ enabledValidation, product });

  const errorFor = useCallback((field: any) => validationErrors[field] || '', [validationErrors]);
  const isValid = useCallback((field: any) => !!validationErrors[field], [validationErrors]);

  const getPriceHelperText = useCallback(() => {
    if (isValid('recurring_value')) return errorFor('recurring_value');
    const isInfo = product.type === EProductType.DIGITAL;
    const isSinglePayment = product.charge_type === EChargeType.SINGLE;
    if (isInfo && isSinglePayment)
      return 'Você poderá cadastrar o preço de venda inclusive para moedas estrangeiras na Edição';
    return 'EX: 1.000,00';
  }, [product.type, product.charge_type, isValid, errorFor]);

  const handleChangeAfterRadio = useCallback(() => {
    handleChange('undetermined_charges', false);
    handleChange('limit_charges', product.limit_charges);
  }, [handleChange, product.limit_charges]);

  const handleChangeRecurrencyType = useCallback(
    (value: string) => {
      handleChange('recurring_frequency_type', value);

      if (value === CUSTOM_RECURRENCY) {
        handleChange('recurring_frequency', 2);
        handleChange('recurring_frequency_type_select', 'MONTH');
        return;
      }

      handleChange('recurring_frequency', 1);
    },
    [handleChange]
  );

  const handleChangePermanentAccess = useCallback(
    (permanentAccess: boolean) => {
      if (permanentAccess) setShowPermanentAccessAlertModal(true);
      handleChange('permanent_access', permanentAccess);
    },
    [handleChange]
  );
  const handleClosePermanentAccessModal = useCallback(() => {
    setShowPermanentAccessAlertModal(false);
  }, []);

  useEffect(() => {
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      prevPropsRef.current = { enabledValidation, product };
      return;
    }

    const prev = prevPropsRef.current;
    if ((prev.enabledValidation === false && enabledValidation) || product !== prev.product)
      setValidationErrors(run(product, Validations));
    prevPropsRef.current = { enabledValidation, product };
  }, [enabledValidation, product]);

  return (
    <>
      <Col xs={24} sm={12} style={{ alignSelf: 'baseline' }}>
        <div className='flex flex-col gap-4'>
          <label className='text-sm'>Moeda</label>
          <span>BRL (Real Brasileiro)</span>
        </div>
      </Col>
      <Col className='sm:flex-1' xs={24} sm={12}>
        <CustomMaskedInput
          label={product.open_price ? 'Valor mínimo' : 'Preço'}
          fullWidth={true}
          disableUnderline={true}
          id='new-product-subscription-value'
          mask='money'
          value={product.recurring_value}
          onChange={(value: any) => handleChange('recurring_value', value)}
          helperText={getPriceHelperText()}
          error={isValid('recurring_value')}
          InputLabelProps={{ shrink: true }}
          startAdornment='R$'
          ref={(ref: any) => setRefField('recurring_value', ref)}
        />
      </Col>
      <Col span={24}>
        <BlockedFieldTooltipProps partnerCod={product.cnt_partner_cod} enabled={isAlumy}>
          <div>
            <label className='mb-1 block text-sm'>Frequência de cobrança</label>
            <Select
              id='new-product-subscription-frequency-type'
              className='w-full'
              value={product.recurring_frequency_type}
              onChange={(value: any) => handleChangeRecurrencyType(value)}
              options={(Object.keys(recurrencyFrequencyType) as Array<keyof typeof recurrencyFrequencyType>).map(
                type => ({
                  label: type,
                  value: recurrencyFrequencyType[type].abbr
                })
              )}
            />
          </div>
        </BlockedFieldTooltipProps>
      </Col>
      {product.recurring_frequency_type === CUSTOM_RECURRENCY && (
        <Col span={24}>
          <BlockedFieldTooltipProps partnerCod={product.cnt_partner_cod} enabled={isAlumy}>
            <>
              <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                <p>Repetir a cada:</p>
                <div style={{ width: '50px', margin: '0 10px', display: 'flex' }}>
                  <CustomMaskedInput
                    id='new-product-subscription-frequency'
                    error={isValid('recurring_frequency')}
                    mask='number'
                    disableUnderline={true}
                    value={product.recurring_frequency}
                    onChange={(value: any) => handleChange('recurring_frequency', value)}
                    disabled={isAlumy}
                  />
                </div>
                <Select
                  onChange={value => handleChange('recurring_frequency_type_select', value)}
                  value={product.recurring_frequency_type_select}
                  options={[
                    { label: product.recurring_frequency > 1 ? 'meses' : 'mês', value: 'MONTH' },
                    { label: product.recurring_frequency > 1 ? 'anos' : 'ano', value: 'YEAR' }
                  ]}
                />
              </div>
              {isValid('recurring_frequency') && (
                <span className='text-xs text-red-500'>{errorFor('recurring_frequency')}</span>
              )}
            </>
          </BlockedFieldTooltipProps>
        </Col>
      )}
      <Col span={24}>
        <BlockedFieldTooltipProps partnerCod={product.cnt_partner_cod} enabled={isAlumy}>
          <>
            <div>A cobrança termina em:</div>
            <div>
              <label className='mb-2 flex items-center gap-2'>
                <Radio
                  checked={product.undetermined_charges}
                  onChange={e => {
                    if (e.target.checked) handleChange('limit_charges', '2');
                    handleChange('undetermined_charges', e.target.checked);
                  }}
                />{' '}
                Nunca
              </label>
              <div>
                <div className='flex items-center gap-2'>
                  <label className='flex items-center gap-2'>
                    <Radio checked={!product.undetermined_charges} onChange={handleChangeAfterRadio} /> Após
                  </label>
                  <div style={{ width: 57 }}>
                    <CustomMaskedInput
                      id='new-product-subscription-limit-charges'
                      mask='number'
                      error={isValid('limit_charges')}
                      onChange={(value: string) => {
                        if (value.length > 2) return;
                        handleChange('limit_charges', value);
                      }}
                      value={product.limit_charges}
                      disabled={isAlumy || product.undetermined_charges}
                    />
                  </div>
                  <span>{product.limit_charges > 1 ? 'cobranças' : 'cobrança'}</span>
                </div>
                {isValid('limit_charges') && <span className='text-xs text-red-500'>{errorFor('limit_charges')}</span>}
              </div>
            </div>
          </>
        </BlockedFieldTooltipProps>
      </Col>
      <Col span={24}>
        <Tooltip
          title={
            product.open_price
              ? OPEN_PRICE_BLOCK_MESSAGE
              : product.type === constants.PHYSICAL
                ? 'A Adesão não pode ser habilitado em produtos físicos'
                : ''
          }
          placement='bottom'
        >
          <div>
            <label className='flex items-center gap-2'>
              <Switch
                id='new-product-subscription-fee'
                disabled={
                  product.open_price ||
                  product.trial ||
                  product.yearly_installment ||
                  product.type === constants.PHYSICAL
                }
                checked={product.membership_fee}
                onChange={checked => handleChange('membership_fee', checked)}
              />
              Taxa de contratação / adesão
            </label>
            <span className='mt-1 block text-xs text-gray-500'>
              {product.open_price ? OPEN_PRICE_BLOCK_MESSAGE : 'O cliente deve pagar taxa de contratação.'}
            </span>
          </div>
        </Tooltip>
      </Col>
      {!!product.membership_fee && (
        <Col span={24}>
          <CustomMaskedInput
            label='Valor de adesão'
            id='new-product-subscription-membership-price'
            mask='money'
            value={product.membership_price}
            onChange={(value: any) => handleChange('membership_price', value)}
            fullWidth={true}
            error={isValid('membership_price')}
            helperText={isValid('membership_price') ? errorFor('membership_price') : 'Ex: R$ 20,00'}
            disableUnderline={true}
            InputLabelProps={{ shrink: true }}
            ref={(ref: any) => setRefField('membership_price', ref)}
          />
        </Col>
      )}
      <Col span={24}>
        <Tooltip
          title={
            product.open_price
              ? OPEN_PRICE_BLOCK_MESSAGE
              : product.membership_fee
                ? 'O Trial não pode ser habilitado junto com a Adesão'
                : product.yearly_installment
                  ? 'O Trial não pode ser habilitado junto com o Parcelamento da Recorrência'
                  : product.type === constants.PHYSICAL
                    ? 'O Trial não pode ser habilitado em produtos físicos'
                    : ''
          }
          placement='bottom'
        >
          <div>
            <label className='flex items-center gap-2'>
              <Switch
                id='new-product-subscription-trial'
                disabled={
                  product.open_price ||
                  product.membership_fee ||
                  product.yearly_installment ||
                  product.type === constants.PHYSICAL
                }
                checked={product.trial}
                onChange={checked => handleChange('trial', checked)}
              />
              Período trial
            </label>
            <span className='mt-1 block text-xs text-gray-500'>
              {product.open_price ? OPEN_PRICE_BLOCK_MESSAGE : 'Permitir que o cliente experimente o produto.'}
            </span>
          </div>
        </Tooltip>
      </Col>
      {product.trial && (
        <Col span={24}>
          <CustomMaskedInput
            label='Dias para trial'
            id='new-product-subscription-trial-days'
            mask='number'
            error={isValid('trial_days')}
            value={product.trial_days}
            onChange={(value: any) => handleChange('trial_days', value)}
            fullWidth={true}
            disableUnderline={true}
          />
          <span className='text-xs text-gray-500'>
            {isValid('trial_days') ? errorFor('trial_days') : 'Ex: 15 dias'}
          </span>
        </Col>
      )}
      {!product.undetermined_charges && (
        <Col span={24}>
          <label className='flex items-center gap-2'>
            <Switch
              id='new-product-subscriptionpermanent-access'
              checked={product.permanent_access}
              onChange={checked => handleChangePermanentAccess(checked)}
            />
            Acesso permanente
          </label>
          <span className='mt-1 block text-xs text-gray-500'>Mantém o acesso mesmo após o término das cobranças.</span>
        </Col>
      )}
      <Col span={24}>
        <CustomMaskedInput
          label='Limite de atraso'
          id='new-product-subscription-overdue-limit'
          mask='number'
          fullWidth={true}
          error={isValid('overdue_limit')}
          helperText={
            isValid('overdue_limit')
              ? errorFor('overdue_limit')
              : `Após ${product.overdue_limit} cobrança(s) consecutivas em atraso, a assinatura será cancelada automaticamente.`
          }
          disableUnderline={true}
          value={product.overdue_limit}
          onChange={(value: any) => handleChange('overdue_limit', value)}
          InputLabelProps={{ shrink: true }}
          ref={(ref: any) => setRefField('overdue_limit', ref)}
        />
      </Col>
      <PermanentAccessAlertModal isOpen={showPermanentAccessAlertModal} handleClose={handleClosePermanentAccessModal} />
    </>
  );
}

export default Subscription;
